import { useState } from "react";
import { useUiPref } from "./ui-prefs.js";

const ANALYTICS_PATHS = new Set([
  "/dashboard",
  "/marketing",
  "/products",
  "/cohorts",
  "/retention",
  "/subscriptions",
  "/operations",
  "/financials",
]);

export function shouldShowGrossResultsNotice(pathname: string): boolean {
  return ANALYTICS_PATHS.has(pathname);
}

export function GrossResultsNoticeCard({
  dismissed,
  onDismiss,
}: {
  dismissed: boolean;
  onDismiss: () => void;
}): React.ReactElement | null {
  if (dismissed) return null;
  return (
    <div
      data-gross-results-notice="true"
      role="status"
      style={{
        marginBottom: 16,
        padding: "11px 13px",
        border: "1px solid color-mix(in srgb, var(--accent) 34%, var(--border))",
        borderRadius: "var(--r-sm)",
        background: "color-mix(in srgb, var(--accent) 7%, var(--surface))",
        color: "var(--body)",
        fontSize: 12.5,
        lineHeight: 1.5,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
      }}
    >
      <span>
        <b style={{ color: "var(--text)" }}>You’re viewing gross results</b>
        <span style={{ display: "block", marginTop: 2 }}>
          These numbers show revenue before product costs, ad spend, or other expenses. Profit views turn on together after every expense is finished and reviewed.
        </span>
      </span>
      <button
        type="button"
        onClick={onDismiss}
        style={{
          border: "1px solid var(--border)",
          borderRadius: 7,
          background: "var(--surface)",
          color: "var(--text)",
          padding: "6px 11px",
          font: "inherit",
          fontWeight: 700,
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        Got it
      </button>
    </div>
  );
}

function PersistedGrossResultsNotice({ accountId }: { accountId: string }): React.ReactElement | null {
  const [dismissed, setDismissed] = useUiPref<boolean>(`gross-results-notice-v1:${accountId}`, false);
  return <GrossResultsNoticeCard dismissed={dismissed} onDismiss={() => setDismissed(true)} />;
}

function SessionGrossResultsNotice(): React.ReactElement | null {
  const [dismissed, setDismissed] = useState(false);
  return <GrossResultsNoticeCard dismissed={dismissed} onDismiss={() => setDismissed(true)} />;
}

/** Never hide the disclosure just because the separate account-name request failed. */
export function GrossResultsNotice({ accountId }: { accountId?: string }): React.ReactElement | null {
  return accountId
    ? <PersistedGrossResultsNotice accountId={accountId} />
    : <SessionGrossResultsNotice />;
}
